/**
 * @deprecated The pair-by-pair duplicates panel is no longer the durable UX
 * for surfacing duplicate information. Per the analyst-interview UX spec
 * (memory: project_ux_decisions):
 *   - The doc-level duplicate signal is now a binary badge that filters the
 *     document list on click, driven by DocumentOverviewItem.isDuplicateOfOtherDoc
 *     (see Badge with type "duplicate", and DocumentInspectHeader.onDuplicateBadgeClick).
 *   - Source provenance for individual duplicates lives inline as a footer
 *     on each page in the main canvas view (Phase 3 of the implementation
 *     plan), replacing the panel-style detail view this modal provided.
 *
 * This component, fetchDuplicates() in services/api.ts, the
 * DuplicatePagesResponse schema, and the GET /datasets/{path}/duplicates
 * route are retained for potential future detail-view reuse and to back the
 * data the binary badge derives from. They are not wired up by default.
 * Do not add new call sites without revisiting the UX decision.
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, AlertCircle, ExternalLink } from 'lucide-react';
import { colors, radii, shadows } from '../theme/tokens';
import { formatLocalDateTime } from '../utils/format';
import { apiService } from '../services/api';
import {
    ModalOverlay,
    ModalPanel,
    ModalHeader,
    ModalTitle,
    ModalCloseButton,
} from './shared/Modal';

// Types matching the backend API schema
export interface DuplicateMatchSide {
    doc_id: string;
    doc_title?: string | null;
    page_id: string;
    page_number?: number | null;
    page_index?: number | null;
    page_name?: string | null;
}

export interface DuplicatePairItem {
    left: DuplicateMatchSide;
    right: DuplicateMatchSide;
    cosine_similarity: number;
}

export interface DuplicateDocumentExample {
    page_id: string;
    page_number: number;
    page_name?: string;
    other_doc_id: string;
    other_doc_title?: string;
    other_page_id: string;
    other_page_number: number;
    cosine_similarity: number;
}

export interface DuplicateDocumentSummary {
    doc_id: string;
    pair_count: number;
    top_examples?: DuplicateDocumentExample[];
}

export interface DuplicatePagesResponse {
    cosine_threshold?: number | null;
    pair_count: number;
    pairs: DuplicatePairItem[];
    documents: DuplicateDocumentSummary[];
    warnings?: string[];
    generated_at?: string | null;
}

interface DuplicatesModalProps {
    isOpen: boolean;
    onClose: () => void;
    datasetPath: string;
    documentId?: string | null;
    onNavigateToDocument?: (docId: string, pageNumber?: number) => void;
    onRemovePage?: (docId: string, pageId: string) => Promise<void>;
    getThumbnailUrl?: (pageId: string) => string | null;
}

const StyledPanel = styled(ModalPanel)`
    padding: 0;
    overflow: hidden;
`;

const Header = styled(ModalHeader)`
    padding: 24px;
    border-bottom: 1px solid ${colors.border.subtle};
`;

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 24px;
`;

const LoadingMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: ${colors.text.muted};
    font-style: italic;
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: ${colors.backgroundTones.danger};
    border: 1px solid ${colors.status.danger};
    border-radius: ${radii.base};
    color: ${colors.status.danger};
    margin-bottom: 16px;
`;

const InfoBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: ${colors.surface.subtle};
    border-radius: ${radii.base};
    margin-bottom: 24px;
    font-size: 14px;
    color: ${colors.text.secondary};
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const InfoLabel = styled.span`
    color: ${colors.text.muted};
`;

const InfoValue = styled.span`
    font-weight: 600;
    color: ${colors.text.primary};
`;

const PairList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const PairCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid ${colors.border.subtle};
    border-radius: ${radii.base};
    background: ${colors.surface.base};
    transition: all 0.2s;

    &:hover {
        box-shadow: ${shadows.level1};
        border-color: ${colors.border.strong};
    }
`;

const PairHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const DistanceChip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: ${colors.surface.subtle};
    border-radius: ${radii.pill};
    font-size: 12px;
    font-weight: 500;
    color: ${colors.text.secondary};
`;

const PairRow = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 16px;
    align-items: center;
`;

const PageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    background: ${colors.surface.subtle};
    border-radius: ${radii.base};
    border: 1px solid ${colors.border.subtle};
`;

const PageInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PageTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
`;

const PageSubtitle = styled.div`
    font-size: 12px;
    color: ${colors.text.muted};
`;

const ThumbnailWrapper = styled.div`
    width: 100%;
    aspect-ratio: 3 / 4;
    background: ${colors.surface.base};
    border: 1px solid ${colors.border.subtle};
    border-radius: ${radii.base};
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ThumbnailImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const ThumbnailPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.text.muted};
    font-size: 12px;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
`;

const DividerIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.text.muted};
    font-size: 20px;
`;

const NavigateButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: ${colors.accents.primaryDark};
    color: ${colors.surface.base};
    border: none;
    border-radius: ${radii.base};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 8px;

    &:hover {
        background: ${colors.accents.primary};
    }
`;

const RemoveButton = styled.button`
    display: flex;
    align-items: center;
    padding: 4px 10px;
    background: transparent;
    color: #c53030;
    border: 1px solid #e0e0e0;
    border-radius: ${radii.base};
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    margin-top: 8px;
    transition: all 0.15s;

    &:hover {
        background: #fef2f2;
        border-color: #c53030;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    color: ${colors.text.muted};
`;

const EmptyStateTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.text.secondary};
    margin-top: 16px;
    margin-bottom: 8px;
`;

const EmptyStateDescription = styled.div`
    font-size: 14px;
    max-width: 400px;
`;

export const DuplicatesModal: React.FC<DuplicatesModalProps> = ({
    isOpen,
    onClose,
    datasetPath,
    documentId,
    onNavigateToDocument,
    getThumbnailUrl,
    onRemovePage,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<DuplicatePagesResponse | null>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const fetchDuplicates = async () => {
            setLoading(true);
            setError(null);

            try {
                const duplicateData = await apiService.fetchDuplicates(datasetPath, documentId ?? undefined);
                setData(duplicateData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Unknown error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchDuplicates();
    }, [isOpen, datasetPath, documentId]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const formatDocumentTitle = (side: DuplicateMatchSide): string => {
        return side.doc_title || side.doc_id;
    };

    const formatPageLabel = (side: DuplicateMatchSide): string => {
        if (side.page_number != null) {
            return `Page ${side.page_number}`;
        }
        if (side.page_name) {
            return side.page_name;
        }
        return side.page_id;
    };

    const thumbnailForSide = (side: DuplicateMatchSide): string | null => {
        if (side.page_index != null && datasetPath) {
            return `/pages/thumb/${side.page_index}?metadata=${encodeURIComponent(datasetPath)}`;
        }
        if (getThumbnailUrl) {
            return getThumbnailUrl(side.page_id);
        }
        return null;
    };

    const filterPairsByDocument = (pairs: DuplicatePairItem[]): DuplicatePairItem[] => {
        if (!documentId) {
            return pairs;
        }
        return pairs.filter(
            pair => pair.left.doc_id === documentId || pair.right.doc_id === documentId
        );
    };

    const handleRemove = async (docId: string, pageId: string, pairIndex: number) => {
        if (!onRemovePage) return;
        try {
            await onRemovePage(docId, pageId);
            // Remove the pair from local state
            setData(prev => {
                if (!prev) return prev;
                const nextPairs = prev.pairs.filter((_, i) => i !== pairIndex);
                return { ...prev, pairs: nextPairs, pair_count: nextPairs.length };
            });
        } catch {
            setError('Failed to remove page.');
        }
    };

    const renderContent = () => {
        if (loading) {
            return <LoadingMessage>Loading duplicate information...</LoadingMessage>;
        }

        if (error) {
            return (
                <ErrorMessage>
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </ErrorMessage>
            );
        }

        if (!data) {
            return <LoadingMessage>No data available</LoadingMessage>;
        }

        const filteredPairs = filterPairsByDocument(data.pairs);

        if (filteredPairs.length === 0) {
            return (
                <EmptyState>
                    <AlertCircle size={48} />
                    <EmptyStateTitle>No duplicates found</EmptyStateTitle>
                    <EmptyStateDescription>
                        {documentId
                            ? 'No duplicate pages detected for this document.'
                            : 'No duplicate pages detected in this case.'}
                    </EmptyStateDescription>
                </EmptyState>
            );
        }

        return (
            <>
                <PairList>
                    {filteredPairs.map((pair, idx) => {
                        const globalIdx = data.pairs.indexOf(pair);
                        return (
                        <PairCard key={idx}>
                            <PairRow>
                                <PageCard>
                                    <ThumbnailWrapper>
                                        {thumbnailForSide(pair.left) ? (
                                            <ThumbnailImage
                                                src={thumbnailForSide(pair.left)!}
                                                alt={formatPageLabel(pair.left)}
                                            />
                                        ) : (
                                            <ThumbnailPlaceholder>
                                                No preview
                                            </ThumbnailPlaceholder>
                                        )}
                                    </ThumbnailWrapper>
                                    <PageInfo>
                                        <PageTitle
                                            onClick={() => onNavigateToDocument?.(pair.left.doc_id, pair.left.page_number ?? undefined)}
                                            style={onNavigateToDocument ? { cursor: 'pointer' } : undefined}
                                        >
                                            {formatDocumentTitle(pair.left)}
                                        </PageTitle>
                                        <PageSubtitle>{formatPageLabel(pair.left)}</PageSubtitle>
                                    </PageInfo>
                                    {onRemovePage && (
                                        <RemoveButton onClick={() => handleRemove(pair.left.doc_id, pair.left.page_id, globalIdx)}>
                                            Remove
                                        </RemoveButton>
                                    )}
                                </PageCard>

                                <DividerIcon>⟷</DividerIcon>

                                <PageCard>
                                    <ThumbnailWrapper>
                                        {thumbnailForSide(pair.right) ? (
                                            <ThumbnailImage
                                                src={thumbnailForSide(pair.right)!}
                                                alt={formatPageLabel(pair.right)}
                                            />
                                        ) : (
                                            <ThumbnailPlaceholder>
                                                No preview
                                            </ThumbnailPlaceholder>
                                        )}
                                    </ThumbnailWrapper>
                                    <PageInfo>
                                        <PageTitle
                                            onClick={() => onNavigateToDocument?.(pair.right.doc_id, pair.right.page_number ?? undefined)}
                                            style={onNavigateToDocument ? { cursor: 'pointer' } : undefined}
                                        >
                                            {formatDocumentTitle(pair.right)}
                                        </PageTitle>
                                        <PageSubtitle>{formatPageLabel(pair.right)}</PageSubtitle>
                                    </PageInfo>
                                    {onRemovePage && (
                                        <RemoveButton onClick={() => handleRemove(pair.right.doc_id, pair.right.page_id, globalIdx)}>
                                            Remove
                                        </RemoveButton>
                                    )}
                                </PageCard>
                            </PairRow>
                        </PairCard>
                        );
                    })}
                </PairList>
            </>
        );
    };

    return (
        <ModalOverlay $visible={isOpen} onClick={handleOverlayClick}>
            <StyledPanel $width="900px">
                <Header>
                    <ModalTitle>
                        {documentId ? 'Possible duplicate pages' : 'Possible duplicate pages'}
                    </ModalTitle>
                    <ModalCloseButton onClick={onClose} aria-label="Close">
                        <X size={20} />
                    </ModalCloseButton>
                </Header>
                <Content>{renderContent()}</Content>
            </StyledPanel>
        </ModalOverlay>
    );
};
